import { FieldValues } from "../Schema";
import { SchemaClient } from "./GraphqlClient";
import { Source } from "./Source";

/**
 * Answers a repeated read from the first answer rather than asking again.
 *
 * Master data does not change while one request is being served, but the schema asks for it
 * repeatedly: a field's option list is a getter, so every equipment a bulk edit touches
 * resolves it afresh. The reviewer list is the expensive case — it reads every user and
 * then one group per prefix of the owning group, which for a hundred equipment is over a
 * thousand reads of data that was identical the first time.
 *
 * Scoped to one instance, so it lives exactly as long as the schema built with it: create
 * it per invocation and the cache cannot go stale within one, or outlast one.
 *
 * This is the same idea as {@link GraphqlClient}'s `cache-first` fetch policy, moved up a
 * level so it applies whichever client is underneath.
 */
export class MemoisedSchemaClient implements SchemaClient {

  public readonly kind: SchemaClient["kind"];

  private readonly client: SchemaClient;

  private readonly answers = new Map<string, Promise<unknown>>();

  private readonly sourceIds = new WeakMap<object, number>();

  private sourceCount = 0;

  /**
   * @param client - The client to read through.
   */
  constructor(client: SchemaClient) {
    this.client = client;
    this.kind = client.kind;
  }

  /**
   * Identifies a read.
   *
   * The source is identified by reference rather than by content: each is declared once as a
   * module constant, so two reads of the same source are two references to the same object,
   * and serialising a GraphQL document to tell them apart would cost more than the read.
   *
   * @param method - Which read.
   * @param source - The source being read.
   * @param variables - The values it binds.
   * @returns A key unique to that read.
   */
  private getKey<T, V>(method: string, source: Source<T, V>, variables: V): string {
    let id = this.sourceIds.get(source as object);
    if (id === undefined) {
      id = this.sourceCount++;
      this.sourceIds.set(source as object, id);
    }
    return `${method}:${id}:${JSON.stringify(variables ?? null)}`;
  }

  /**
   * Reads a list, from the cache when the same list has already been asked for.
   *
   * The promise is cached rather than its result, so reads issued concurrently — which is
   * how the schema resolves its option getters — share one round trip instead of racing.
   *
   * @param source - The source to read.
   * @param variables - The values its expressions bind.
   * @returns The items.
   */
  public async queryList<T, V = FieldValues>(
    source: Source<T[], V>,
    variables: V = {} as V,
  ): Promise<T[] | null> {
    const key = this.getKey("queryList", source, variables);
    if (!this.answers.has(key)) {
      // A failed read is not cached: the next attempt should be allowed to succeed.
      const answer = this.client.queryList(source, variables).catch((error) => {
        this.answers.delete(key);
        throw error;
      });
      this.answers.set(key, answer);
    }
    return this.answers.get(key) as Promise<T[] | null>;
  }

  /**
   * Reads a single item, from the cache when it has already been asked for.
   *
   * @param source - The source to read.
   * @param variables - The values its key binds.
   * @returns The item, or `null` when there is none.
   */
  public async query<T, V = FieldValues>(
    source: Source<T, V>,
    variables: V = {} as V,
  ): Promise<T | null> {
    const key = this.getKey("query", source, variables);
    if (!this.answers.has(key)) {
      const answer = this.client.query(source, variables).catch((error) => {
        this.answers.delete(key);
        throw error;
      });
      this.answers.set(key, answer);
    }
    return this.answers.get(key) as Promise<T | null>;
  }
}
